import { useCallback, useEffect, useRef } from 'react';
import { useReportClientErrorMutation } from '../redux/services/client_errors.jsx';

// how long the editor may take to start before it is reported as not started
const EDITOR_START_TIMEOUT_MS = 15000;

const TINYMCE_LOAD_ERROR_EVENTS =
  'PluginLoadError ThemeLoadError ModelLoadError LanguageLoadError IconsLoadError SkinLoadError';

/**
 * State of the page and TinyMCE useful for finding out why the editor did not start.
 *
 * @returns {object}
 */
const collectDiagnostics = () => {
  const tinymce = window.tinymce;

  return {
    tinymceLoaded: !!tinymce,
    tinymceVersion: tinymce ? `${tinymce.majorVersion}.${tinymce.minorVersion}` : null,
    editorCount: tinymce?.get?.().length ?? null,
    initializedEditorCount:
      tinymce?.get?.().filter((editor) => editor.initialized).length ?? null,
    documentVisibility: document.visibilityState,
    online: navigator.onLine,
  };
};

/**
 * Reports to the backend (storage/logs/frontend.log) when the TinyMCE editor fails to start,
 * so the cause of a plain textarea shown instead of the editor can be found.
 *
 * @returns {{onInit: function, onScriptsLoadError: function, bindEditorErrorEvents: function}}
 *   props for the TinyMCE Editor component and a function to call in the editor setup
 */
const useTinyMceErrorReporting = () => {
  const [reportClientError] = useReportClientErrorMutation();
  const isInitializedRef = useRef(false);

  const report = useCallback(
    (message, details = {}) => {
      reportClientError({
        source: 'tinymce',
        message,
        url: window.location.href,
        details: { ...collectDiagnostics(), ...details },
      });
    },
    [reportClientError],
  );

  useEffect(() => {
    const timer = window.setTimeout(() => {
      if (!isInitializedRef.current) {
        report(`Editor did not start within ${EDITOR_START_TIMEOUT_MS / 1000} s`);
      }
    }, EDITOR_START_TIMEOUT_MS);

    return () => window.clearTimeout(timer);
  }, [report]);

  const onInit = useCallback(() => {
    isInitializedRef.current = true;
  }, []);

  const onScriptsLoadError = useCallback(
    (error) => {
      report('TinyMCE script failed to load', {
        error: String(error?.message ?? error),
      });
    },
    [report],
  );

  const bindEditorErrorEvents = useCallback(
    (editor) => {
      editor.on(TINYMCE_LOAD_ERROR_EVENTS, (event) => {
        report(`${event.type}: ${event.message ?? ''}`);
      });
    },
    [report],
  );

  return { onInit, onScriptsLoadError, bindEditorErrorEvents };
};

export default useTinyMceErrorReporting;
